import React, { useState, useEffect } from 'react';
import Sidebar from '../components/shared/Sidebar';
import DataGrid, { GridColumn } from '../components/data/DataGrid';
import { RoleManagementService, FeatureFlag, FeatureWithAssignment } from '../services/roleManagementService';
import { FeatureService } from '../services/featureService';
import './RoleMaintenancePage.css';

// ============================================================================
// FEATURE ROLE MAINTENANCE PAGE
// ============================================================================
// This page manages Feature Roles (what users can DO) using the feature_roles
// and role_feature_flags tables. Feature roles are now organization-specific.
// This is separate from Data Access Roles (what data users can access).
// ============================================================================

interface Role {
  id: string;
  name: string;
  description?: string;
  organization_id?: number;
  created_at: string;
  updated_at: string;
}

// Using FeatureFlag interface from the service

interface AvailableFeature {
  key: string;
  name: string;
  description: string;
}

const RoleMaintenancePage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [selectedRoleForFeatures, setSelectedRoleForFeatures] = useState<Role | null>(null);
  const [roleFeatures, setRoleFeatures] = useState<FeatureWithAssignment[]>([]);
  const [availableFeatures, setAvailableFeatures] = useState<AvailableFeature[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchRoles();
    fetchAvailableFeatures();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const roles = await RoleManagementService.getAllRoles();
      setRoles(roles);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableFeatures = async () => {
    try {
      setFeaturesLoading(true);
      const features = await FeatureService.getEnabledFeatures();
      const mappedFeatures = features.map(feature => ({
        key: feature.key,
        name: feature.name,
        description: feature.description || `Access to ${feature.name}`
      }));
      
      setAvailableFeatures(mappedFeatures);
    } catch (err) {
      console.error('Error fetching available features:', err);
      // Fallback to empty array - role management will still work
      setAvailableFeatures([]);
    } finally {
      setFeaturesLoading(false);
    }
  };

  const fetchRoleFeatures = async (roleId: string) => {
    try {
      const features = await RoleManagementService.getRoleFeatures(roleId);
      setRoleFeatures(features);
    } catch (err) {
      console.error('Error fetching role features:', err);
      setRoleFeatures([]);
    }
  };

  const generateUniqueRoleId = (): string => {
    // Generate a unique role ID using timestamp and random string
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    return `role_${timestamp}_${randomStr}`;
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setFormData({
      id: generateUniqueRoleId(),
      name: '',
      description: ''
    });
    setShowRoleModal(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setFormData({
      id: role.id,
      name: role.name,
      description: role.description || ''
    });
    setShowRoleModal(true);
  };

  const handleDeleteRole = async (role: Role) => {
    if (!window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
      return;
    }

    try {
      await RoleManagementService.deleteRole(role.id);
      await fetchRoles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role');
    }
  };

  const handleManageFeatures = async (role: Role) => {
    setSelectedRoleForFeatures(role);
    setFeaturesLoading(true);
    await fetchRoleFeatures(role.id);
    setFeaturesLoading(false);
    setShowFeatureModal(true);
  };

  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingRole) {
        await RoleManagementService.updateRole(editingRole.id, {
          name: formData.name,
          description: formData.description
        });
      } else {
        await RoleManagementService.createRole({
          id: formData.id,
          name: formData.name,
          description: formData.description
        });
      }

      setShowRoleModal(false);
      await fetchRoles();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${editingRole ? 'update' : 'create'} role`);
    }
  };

  const handleToggleFeature = async (featureKey: string, enabled: boolean) => {
    if (!selectedRoleForFeatures) return;

    try {
      // Optimistically update the UI immediately
      setRoleFeatures(prev => 
        prev.map(feature => 
          feature.key === featureKey 
            ? { ...feature, assigned: enabled }
            : feature
        )
      );

      await RoleManagementService.updateRoleFeature(selectedRoleForFeatures.id, {
        featurekey: featureKey,
        enabled
      });

      // Refresh the data to ensure consistency
      await fetchRoleFeatures(selectedRoleForFeatures.id);
    } catch (err) {
      // If the request failed, revert the optimistic update
      await fetchRoleFeatures(selectedRoleForFeatures.id);
      setError(err instanceof Error ? err.message : 'Failed to update feature flag');
    }
  };

  const columns: GridColumn[] = [
    { field: 'id', headerName: 'Role ID', width: 150 },
    { field: 'name', headerName: 'Role Name', width: 200 },
    { field: 'description', headerName: 'Description', width: 300 },
    { field: 'organization_id', headerName: 'Org ID', width: 100 },
    { 
      field: 'created_at', 
      headerName: 'Created', 
      width: 150,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : ''
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      renderCell: (row) => {
        const role = row as Role;
        return (
          <div className="role-actions">
            <button 
              className="btn-edit"
              onClick={() => handleEditRole(role)}
              title="Edit Role"
              disabled={true}
            >
              Edit (N/A)
            </button>
            <button 
              className="btn-features"
              onClick={() => handleManageFeatures(role)}
              title="Manage Features"
            >
              Features
            </button>
            <button 
              className="btn-delete"
              onClick={() => handleDeleteRole(role)}
              title="Delete Role"
              disabled={true}
            >
              Delete (N/A)
            </button>
          </div>
        );
      }
    }
  ];

  const getFeatureStatus = (featureKey: string): boolean => {
    // Find the feature by key and return its assignment status
    const feature = roleFeatures.find(rf => rf.key === featureKey);
    return feature ? feature.assigned : false;
  };

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <div className="loading">Loading roles...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <div className="role-maintenance-page">
          <div className="page-header">
            <h1>Role Maintenance</h1>
            <p>Create and manage custom roles with feature permissions</p>
          </div>

          {error && (
            <div className="error-alert">
              <strong>Error:</strong> {error}
              <button onClick={() => setError(null)} className="close-btn">×</button>
            </div>
          )}

          <div className="page-actions">
            <button className="btn-primary" onClick={handleCreateRole}>
              Create New Role
            </button>
          </div>

          <div className="roles-grid">
            {roles.length === 0 && !loading ? (
              <div className="empty-message">
                No roles found. Create your first custom role to get started.
              </div>
            ) : (
              <DataGrid
                rows={roles}
                columns={columns}
                loading={loading}
              />
            )}
          </div>

          {/* Role Create/Edit Modal */}
          {showRoleModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>{editingRole ? 'Edit Role' : 'Create New Role'}</h3>
                  <button onClick={() => setShowRoleModal(false)} className="close-btn">×</button>
                </div>
                <form onSubmit={handleSaveRole} className="role-form">
                  <div className="form-group">
                    <label htmlFor="roleId">Role ID:</label>
                    <input
                      type="text"
                      id="roleId"
                      value={formData.id}
                      onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                      placeholder="Auto-generated unique ID"
                      required
                      disabled={editingRole !== null}
                      readOnly={editingRole !== null}
                    />
                    <small>{editingRole ? 'Role ID cannot be changed after creation' : 'Unique identifier for the feature role'}</small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="roleName">Role Name:</label>
                    <input
                      type="text"
                      id="roleName"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., CSV Importer, Report Generator"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="roleDescription">Description:</label>
                    <textarea
                      id="roleDescription"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe what features this role provides access to..."
                      rows={3}
                    />
                  </div>
                  <div className="form-actions">
                    <button type="button" onClick={() => setShowRoleModal(false)} className="btn-secondary">
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      {editingRole ? 'Update Role' : 'Create Role'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Feature Management Modal */}
          {showFeatureModal && selectedRoleForFeatures && (
            <div className="modal-overlay">
              <div className="modal-content feature-modal">
                <div className="modal-header">
                  <h3>Manage Features: {selectedRoleForFeatures.name}</h3>
                  <button onClick={() => setShowFeatureModal(false)} className="close-btn">×</button>
                </div>
                <div className="feature-list">
                  <div className="feature-header">
                    <p>Select which features this role should have access to:</p>
                  </div>
                  {featuresLoading ? (
                    <div className="loading">Loading features...</div>
                  ) : roleFeatures.length === 0 ? (
                    <div className="empty-message">No features available</div>
                  ) : (
                    roleFeatures.map(feature => (
                    <div key={feature.key} className="feature-item">
                      <label className="feature-checkbox">
                        <input
                          type="checkbox"
                          checked={feature.assigned}
                          onChange={(e) => handleToggleFeature(feature.key, e.target.checked)}
                        />
                        <span className="feature-name">{feature.name}</span>
                      </label>
                      <span className="feature-description">{feature.description}</span>
                    </div>
                    ))
                  )}
                </div>
                <div className="form-actions">
                  <button onClick={() => setShowFeatureModal(false)} className="btn-primary">
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleMaintenancePage;